import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Package, Mail } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { API, useCart } from '../App';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await fetch(`${API}/checkout/status/${sessionId}`);
          if (response.ok) {
            const data = await response.json();
            setPaymentData(data);

            if (data.payment_status === 'paid') {
              setStatus('success');
              clearCart();
              return;
            } else if (data.status === 'expired') {
              setStatus('expired');
              return;
            }
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus('pending');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus('error');
          }
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId, clearCart]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <Loader2 className="w-16 h-16 text-[#C5A059] mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-[#2C2C2C] mb-4">Processing Payment...</h1>
            <p className="text-[#595959]">Please wait while we confirm your payment.</p>
          </motion.div>
        );

      case 'success':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#5D6D53]/10 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-[#5D6D53]" />
            </div>
            <h1 className="font-serif text-4xl text-[#2C2C2C] mb-4" data-testid="success-title">
              Thank You!
            </h1>
            <p className="text-xl text-[#595959] mb-2">Your order has been confirmed.</p>
            {paymentData && (
              <p className="text-[#C5A059] font-semibold text-lg mb-8">
                Total: £{(paymentData.amount_total / 100).toFixed(2)}
              </p>
            )}

            <div className="bg-[#F5F1EB] rounded-xl p-6 mb-8 max-w-md mx-auto">
              <div className="flex items-start gap-4 text-left">
                <Mail className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-[#2C2C2C] mb-1">Confirmation Email</h3>
                  <p className="text-sm text-[#595959]">
                    We've sent a confirmation email with your order details. Please check your inbox.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-8 max-w-md mx-auto border border-stone-100">
              <div className="flex items-start gap-4 text-left">
                <Package className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-[#2C2C2C] mb-1">What's Next?</h3>
                  <p className="text-sm text-[#595959]">
                    We're preparing your order with care. You'll receive a shipping notification once your candles are on their way!
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/shop">
                <Button className="btn-primary">Continue Shopping</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="btn-secondary">Back to Home</Button>
              </Link>
            </div>
          </motion.div>
        );

      case 'expired':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4A359]/10 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-[#D4A359]" />
            </div>
            <h1 className="font-serif text-3xl text-[#2C2C2C] mb-4">Session Expired</h1>
            <p className="text-[#595959] mb-8">Your payment session has expired. Please try again.</p>
            <Link to="/cart">
              <Button className="btn-primary">Return to Cart</Button>
            </Link>
          </motion.div>
        );

      case 'pending':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4A359]/10 flex items-center justify-center">
              <Loader2 className="w-12 h-12 text-[#D4A359]" />
            </div>
            <h1 className="font-serif text-3xl text-[#2C2C2C] mb-4">Payment Processing</h1>
            <p className="text-[#595959] mb-8">
              Your payment is still being processed. Please check your email for confirmation.
            </p>
            <Link to="/">
              <Button className="btn-primary">Back to Home</Button>
            </Link>
          </motion.div>
        );

      case 'error':
      default:
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#B94A48]/10 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-[#B94A48]" />
            </div>
            <h1 className="font-serif text-3xl text-[#2C2C2C] mb-4">Something Went Wrong</h1>
            <p className="text-[#595959] mb-8">
              We couldn't verify your payment. If you've been charged, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cart">
                <Button className="btn-primary">Return to Cart</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="btn-secondary">Contact Support</Button>
              </Link>
            </div>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6 py-24" data-testid="checkout-success-page">
      <div className="max-w-2xl w-full">
        {renderContent()}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
